import { getBotSenderId } from 'modules/embed/utils'
import { pathOr } from 'ramda'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Conversation } from 'types/conversation'
import { getRandomTeamMemberId } from 'utils/team'

import useActiveCampaign from './useActiveCampaign'
import useActiveConversation from "./useActiveConversation"
import useEmbedThemeProperties from './useEmbedThemeProperties'
import useLastAgentMessage from './useLastAgentMessage'

const getWelcomeMessageSenderId = (campaignSenderId: number): number => {
  if (campaignSenderId === 0) {
    return getRandomTeamMemberId()
  }

  return campaignSenderId
}

const getSenderIdsFromEmbedTheme = (userList: number[] = [], userListMode: string = "RANDOM", autoAssigneeId: number): number[] => {
  if (!!autoAssigneeId) {
    return [autoAssigneeId]
  }

  if (userListMode === "RANDOM") {
    return getRandomTeamMemberId(3)
  }

  if (!userList.length || userList.includes(null)) {
    return getRandomTeamMemberId()
  }

  if (!!userList.length) {
    return [userList[0]]
  }

  return null
}

const getSenderIdFromConversation = (activeConversation: Partial<Conversation> = {}, authorId: number): number => {
  if (!!authorId) {
    return authorId
  }

  if (!!activeConversation && !!activeConversation.assigneeId) {
    return activeConversation.assigneeId
  }

  return null
}

/**
 * Resolves up to three current agent senderIds given the following inferences:
 *
 * 1) Will look at the last agent message in redux for the active conversation ID. If there is an author id, we will use that
 * 2) In the case of a null last agent message, we will look at the messages for the current conversation and pull the last authorId for messages with authorType "USER"
 * 3) Looks to see if there's an automatic sender and if so, use that authorId
 * 4) Looks to see if there is an active campaign with a senderId
 * 4) Tries to get the sender from the embed theme
 */
export const useActiveSenderIds = () : number[] => {
  const activeConversation = useActiveConversation()
  const lastAgentMessage = useLastAgentMessage()
  const activeCampaign = useActiveCampaign()
  const [userList = [], userListMode = "RANDOM"] = useEmbedThemeProperties(['userList', 'userListMode'])

  const { configuration = {} } = useSelector(state => state.embed)
  const { autoAssigneeId } = configuration

  const { assignedAgentId } = useSelector(state => state.session)

  const authorId = useMemo(() => pathOr<number>(null, ['authorId'], lastAgentMessage), [lastAgentMessage])
  const campaignSenderId = useMemo(() => pathOr(null, ['attributes', 'senderId'], activeCampaign), [activeCampaign])
  const campaignAuthorId = useMemo(() => pathOr<number>(null, ['authorId'], activeCampaign), [activeCampaign])
  const isAutomaticSender = useMemo(() => !!pathOr(null, ['attributes', 'automaticSender'], activeCampaign), [activeCampaign])

  const hasDriftlinkBotSender = useMemo(() => pathOr(false, ['attributes', 'isFirstMessage'], activeCampaign) && !!pathOr(null, ['attributes', 'startInteraction'], activeCampaign), [activeCampaign])
  const hasAutomaticSender = isAutomaticSender && !!campaignAuthorId
  const conversationSenderId = getSenderIdFromConversation(activeConversation, authorId)

  if (!!conversationSenderId) {
    return [conversationSenderId]
  }

  if (assignedAgentId) {
    return [assignedAgentId]
  }

  if (hasAutomaticSender) {
    return [campaignAuthorId]
  }

  if (hasDriftlinkBotSender) {
    return [getBotSenderId()]
  }

  if(!!campaignSenderId) {
    return [getWelcomeMessageSenderId(campaignSenderId)]
  }
  return getSenderIdsFromEmbedTheme(userList, userListMode, autoAssigneeId)
}

/**
 * Resolves the current agent senderId given the following inferences:
 *
 * 1) Will look at the last agent message in redux for the active conversation ID. If there is an author id, we will use that
 * 2) In the case of a null last agent message, we will look at the messages for the current conversation and pull the last authorId for messages with authorType "USER"
 * 3) Looks to see if there's an automatic sender and if so, use that authorId
 * 4) Looks to see if there is an active campaign with a senderId
 * 4) Tries to get the sender from the embed theme
 */
export const useActiveSenderId = () => {
  const ids = useActiveSenderIds()
  return ids[0]
}
